.page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--color-background-primary);
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  padding: 0 map.get($spacing-scale, 'xl');
}

.wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  padding: map.get($spacing-scale, 'lg') 0;
  flex: 1;

  @media (min-width: $breakpoint-md) {
    height: calc(100dvh - 100px);
    flex: none;
  }
}

// Page Header with title and filters
.pageHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: map.get($spacing-scale, 'lg');
  background-color: var(--color-background-primary);
  flex-shrink: 0;
  z-index: 2;
}

.title {
  @include body-large-medium;
  color: var(--color-text-primary);
  margin: 0;
}

.filters {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 'sm');
}

// Main content area
.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background-color: var(--color-surface-primary);
  border: 1px solid var(--color-grey-soft);
  border-radius: map.get($radius, 'lg');
  overflow: hidden;
}

// Empty state container (centered)
.emptyStateContainer {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: map.get($spacing-scale, 'xl');
}

// Results layout - two columns
.resultsLayout {
  display: flex;
  flex: 1;
  min-height: 0;
}

// Left sidebar - site list
.siteList {
  width: 340px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--color-border);
  background: var(--color-surface-primary);
}

.filterRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: map.get($spacing-scale, 'sm') map.get($spacing-scale, 'md');
  border-bottom: 1px solid var(--color-border);
  min-height: 52px;
}

.countLabel {
  @include metadata-medium;
  color: var(--color-text-secondary);
}

.countFiltered {
  color: var(--color-primary-base);
}

.filterActive {
  color: var(--color-primary-base);
}

.siteListHeader {
  display: flex;
  align-items: stretch;
  position: sticky;
  top: 0;
  z-index: 1;
  flex-shrink: 0;
}

.siteListHeaderName {
  flex: 1;
}

.siteListHeaderUrls {
  width: 100px;

  // Right-align text in TableHeaderCell
  span {
    text-align: right;
  }
}

.siteListItems {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.noResultsState {
  padding: map.get($spacing-scale, 'xl') map.get($spacing-scale, 'md');
  display: flex;
  justify-content: center;
}

// No results state in site list
.noResultsState {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: map.get($spacing-scale, 'lg');
}

// Right side - site details
.siteDetails {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.siteDetailsHeader {
  flex-shrink: 0;
  background: var(--color-surface-primary);
}

// Action Bar - below site details header
.actionBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: map.get($spacing-scale, 'md') map.get($spacing-scale, 'lg');
  background: var(--color-surface-primary);
  border-bottom: 1px solid var(--color-grey-soft);
  flex-shrink: 0;
}

.actionBarLeft {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 'md');

  &.disabled {
    opacity: 0.5;
  }
}

.actionBarRight {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 'sm');
}

.noSiteSelected {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: map.get($spacing-scale, 'xl');
}

// Details content area - two columns for profiles and contacts
.detailsContent {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

// Combined empty state when both profiles and contacts are empty
.combinedEmptyState {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: map.get($spacing-scale, 'xl');
  position: relative;
}

.combinedEmptyStateHeader {
  position: absolute;
  top: map.get($spacing-scale, 'md');
  right: map.get($spacing-scale, 'md');
}

.profilesColumn {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 's-2');
  padding: map.get($spacing-scale, 'lg');
  overflow-y: auto;
  border-right: 1px solid var(--color-grey-soft);
}

// Section title row (title + add button)
.sectionTitleRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 map.get($spacing-scale, 'md') 0;

  .sectionTitle {
    margin: 0;
  }
}

// Section title for contacts list
.sectionTitle {
  @include body-medium;
  color: var(--color-text-primary);
  margin: 0 0 map.get($spacing-scale, 'md') 0;
}

// Section title with extra top gap (when following another section)
.sectionTitleWithGap {
  margin-top: map.get($spacing-scale, 'lg');
}

.contactsColumn {
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 's-2');
  padding: map.get($spacing-scale, 'lg');
  overflow-y: auto;
  flex-shrink: 0;
}

// Add menu dropdown (Add Contact / Add WhatsApp)
.addMenuWrapper {
  position: relative;
}

.addMenu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: map.get($spacing-scale, 'xs');
  min-width: 180px;
  background-color: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 10;
  overflow: hidden;
}

.addMenuItem {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 's-3');
  width: 100%;
  padding: map.get($spacing-scale, 's-2') map.get($spacing-scale, 'md');
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-primary);
  white-space: nowrap;

  @include body-small-medium;
  @include transition(background-color);

  &:hover {
    background-color: var(--color-grey-light);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary-base);
    outline-offset: -2px;
  }
}

.addMenuWhatsappIcon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  img {
    width: 20px;
    height: 20px;
  }
}

// Empty state in profiles column when no contacts but socials exist
.emptyProfilesState {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: map.get($spacing-scale, 'xl');
}

// Responsive adjustments
@media (max-width: $breakpoint-md) {
  .main {
    padding: 0 map.get($spacing-scale, 'md');
  }

  .pageHeader {
    flex-direction: column;
    align-items: flex-start;
    gap: map.get($spacing-scale, 'md');
  }

  .filters {
    flex-wrap: wrap;
  }

  .resultsLayout {
    flex-direction: column;
  }

  .siteList {
    width: 100%;
    max-height: 300px;
    border-right: none;
    border-bottom: 1px solid var(--color-grey-soft);
  }

  .detailsContent {
    flex-direction: column;
  }

  .profilesColumn {
    border-right: none;
    border-bottom: 1px solid var(--color-grey-soft);
  }

  .contactsColumn {
    width: 100%;
  }
}
